import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import camelCase from 'lodash/camelCase';
import first from 'lodash/first';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import merge from 'lodash/merge';
import transform from 'lodash/transform';
import titleize from 'titleize';

import { DASHBOARD_RENEWAL_ACTION } from './constants';

dayjs.extend(minMax);

/**
 * This function will take either an object or an array of objects
 * then iterate over all keys and camel case said keys.
 */
export function camelCaseDictKeys(json) {
  return transform(json, (acc, value, key, target) => {
    const camelKey = isArray(target) ? key : camelCase(key);
    acc[camelKey] = isObject(value) ? camelCaseDictKeys(value) : value;
  });
}

export function parseRawUserLicensesDataAsActiveLicenses(raw) {
  return camelCaseDictKeys(raw);
}

/**
 * This function will generate an object for the "ActionableItems" component
 * to consume to render a bulk renewal action onto the "Dashboard" component.
 *
 * @param {*} userLicensesData This is the data from "/renewableLicenses" API
 * @param {string} verbiage The language used by the jurisdiction for their permits
 */
export function parseRawUserLicensesDataAsAction(
  userLicensesData, // This data comes in as camel cased.
  verbiage,
) {
  if (isEmpty(userLicensesData)) return [];

  /**
   * Generate a user friendly string for either a single permit
   * or multiple permits.
   */
  const label =
    userLicensesData.length <= 1
      ? `Renew ${verbiage} - ${titleize(
          first(userLicensesData).propertyAddress,
        )}`
      : `Renew ${verbiage} - ${userLicensesData.length} properties`;

  /**
   * Get the earliest expiration date (in unix time) for all renewable permits.
   * This will be used to tell the user the date at which the first permit of the
   * set will expire.
   */
  const earliestExpirationDate = userLicensesData
    .reduce((earliestDate, { expirationDate }) => {
      if (!earliestDate) return dayjs(expirationDate);
      if (dayjs(expirationDate).isValid())
        return dayjs.min(dayjs(earliestDate), dayjs(expirationDate));
      return earliestDate;
    }, null)
    .unix();

  return [
    {
      label,
      dueDate: earliestExpirationDate,
      isBulkRenewal: userLicensesData.length > 1,
      type: DASHBOARD_RENEWAL_ACTION,
    },
  ];
}

/**
 * This function add context to the permits that are selected for bulk renewal.
 */
export function parseBulkLicenseRenewalData(raw, bulkRenewableLicenses) {
  const perLicenseRenewalDetails = camelCaseDictKeys(raw.details);

  const updatedBulkRenewalData = perLicenseRenewalDetails.map((details) => {
    const original = bulkRenewableLicenses.find(
      (d) => d.license === details.licenceNo, // ! The typo is on purpose for "licenceNo"
    );

    return merge({}, original, {
      licenseFee: details.licenseFee,
      newExpirationDate: details.newExpirationDate,
    });
  });

  return updatedBulkRenewalData;
}
